import * as styles from "./SelectContent.css";

import { clsx } from "clsx";
import { type CSSProperties, type JSX, useEffect } from "react";

import { Select as BaseSelect } from "@base-ui/react/select";
import { popupWidthStyle } from "@frontend/utils/popupWidth";

import { useSelectContext } from "./Select.context";
import type { SelectContentProps } from "./Select.types";
import { SelectBackdrop } from "./SelectBackdrop";
import { SelectPortal } from "./SelectPortal";
import {
  SelectScrollDownArrow,
  SelectScrollUpArrow,
} from "./SelectScrollArrow";
import { SELECT_LEFT_INSET_VAR } from "./constants";

export function SelectContent({
  className,
  children,
  style,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  alignItemWithTrigger: alignItemWithTriggerProp,
  positionMethod,
  popupWidth: popupWidthProp,
  ...props
}: SelectContentProps): JSX.Element {
  const { size, layout, leftInset } = useSelectContext();

  const isText = layout === "text";

  // An inline trigger is only a few characters wide, so the popup sizes itself
  const alignItemWithTrigger = alignItemWithTriggerProp ?? !isText;
  const popupWidth = popupWidthProp ?? (isText ? "content" : undefined);

  const width = alignItemWithTrigger ? undefined : popupWidth;

  useEffect(() => {
    if (alignItemWithTrigger && popupWidth !== undefined) {
      console.warn(
        "[Select] popupWidth is ignored while alignItemWithTrigger is on, which keeps the popup on the trigger's width. Set alignItemWithTrigger={false} to use it.",
      );
    }
  }, [alignItemWithTrigger, popupWidth]);

  return (
    <SelectPortal>
      <SelectBackdrop />
      <BaseSelect.Positioner
        data-slot="select-positioner"
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        positionMethod={positionMethod}
        arrowPadding={8}
        className={styles.positioner}
      >
        <BaseSelect.Popup
          data-slot="select-popup"
          data-align-trigger={alignItemWithTrigger}
          data-size={size}
          style={(state) =>
            ({
              ...popupWidthStyle(width),
              ...(typeof style === "function" ? style(state) : style),
              [SELECT_LEFT_INSET_VAR]: `${leftInset}px`,
            }) as CSSProperties
          }
          className={clsx(styles.popup, className)}
          {...props}
        >
          <SelectScrollUpArrow />
          <BaseSelect.List data-slot="select-list" className={styles.list}>
            {children}
          </BaseSelect.List>
          <SelectScrollDownArrow />
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </SelectPortal>
  );
}
